import {
    type DepositAccount,
    RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP,
    type RecurringTransferFrequency,
} from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { fromZonedTime } from 'date-fns-tz';
import { Link, Redirect, router, useLocalSearchParams } from 'expo-router';
import { useAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckIcon,
    FullScreenSpinner,
    H2Emphasized,
    Icon,
    isWeb,
    Private,
    ScreenContainer,
    Sheet,
    TextRow,
    type TextRowProps,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useUpdateBankingAdoption } from '../../../bankingSwitch/hooks/useUpdateBankingAdoption';
import { BankingAdoptionTaskStatus } from '../../../bankingSwitch/types/BankingAdoption';
import { assertNotNullish, isEmpty } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import {
    BankingExperience,
    useBankingExperience,
} from '../../../common/providers/BankingExperienceProvider';
import {
    centsToFormattedDollars,
    getNormalizedDepositAccountNickname,
    getRecurringTransferEndText,
} from '../../../common/utils/bankingUtils';
import {
    DAY_OF_WEEK_FORMAT,
    DEFAULT_DATE_FORMAT,
    format,
    formatInTimezone,
    formatReadableMonthDayInTimezone,
    SHORT_READABLE_DATE_FORMAT,
    TIME_FORMAT_WITH_TIMEZONE,
} from '../../../common/utils/dateUtils';
import FetchError from '../../../http/FetchError';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useCreateDepositoryTransfer, {
    type DepositoryTransferRequest,
} from '../../hooks/useCreateDepositoryTransfer';
import useCreatePaymentSchedule, {
    type CreatePaymentScheduleRequest,
} from '../../hooks/useCreatePaymentSchedule';
import useGetACHTransferExpectedArrival from '../../hooks/useGetACHTransferExpectedArrival';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import useGetAllBankAccountDetailsLegacy from '../../hooks/useGetAllBankAccountDetailsLegacy';
import { transferRequestAtom } from '../../state/TransferAtoms';
import {
    ConfirmTransferDisclosure,
    DEFAULT_RECURRING_EXECUTION_TIME_24H,
    EBBankServicesDisclosure,
    EXTERNAL_INCOMING_TRANSFER_TIMELINE_DISCLOSURE_TEXT,
    EXTERNAL_OUTGOING_TRANSFER_TIMEFRAME_DISCLOSURE_TEXT,
    ONE_TIME_INTERNAL_TRANSFER_DISCLOSURE_TEXT,
    OneTimeExternalTransferDisclosureFootnote,
    OneTimeInternalTransferDisclosureFootnote,
    RecurringExternalTransferDisclosureFootnote,
    RecurringInternalTransferDisclosureFootnote,
    TokenizedAccountNumberDisclosure,
} from '../Disclosures';
import { TransferAccountIcons } from './TransferIcons';

const TransferConfirmation = () => {
    LogRocketFacade.tagPage('TransferConfirmation');

    const { context } = useLocalSearchParams();
    const isFirstFunding = context === 'firstFunding';

    const user = useAuthedUser();
    const { bankingExperience } = useBankingExperience();
    const toast = useToastController();

    const [transferRequest, setTransferRequest] = useAtom(transferRequestAtom);

    const [transferSubmitted, setTransferSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [
        isTransferConfirmErrorSheetOpen,
        setIsTransferConfirmErrorSheetOpen,
    ] = useState(false);
    const [onContinue, setOnContinue] = useState(() => () => {});

    const queryClient = useQueryClient();

    const { mutate: updateBankingAdoption } = useUpdateBankingAdoption({
        onSuccess: (newData) => {
            void queryClient.setQueryData(['getBankingAdoption'], newData);
        },
    });

    const { mutateAsync: createDepositoryTransfer } =
        useCreateDepositoryTransfer({
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['getAllAccountDetailsV2'],
                });
                await queryClient.invalidateQueries({
                    queryKey: [`getAllAccountDetails`],
                });
                updateBankingAdoption({
                    body: {
                        accountFundedStatus: BankingAdoptionTaskStatus.COMPLETE,
                    },
                });
            },
        });

    const { mutateAsync: createPaymentSchedule } = useCreatePaymentSchedule({
        onSuccess: () => {
            updateBankingAdoption({
                body: {
                    accountFundedStatus: BankingAdoptionTaskStatus.COMPLETE,
                },
            });
        },
    });

    const { data: expectedArrivalDateData } = useGetACHTransferExpectedArrival({
        date: new Date().toISOString(),
    });

    const expectedArrivalDateZoned = expectedArrivalDateData
        ? fromZonedTime(
              `${expectedArrivalDateData.expectedArrivalDate} 18:00:00`,
              'America/New_York',
          )
        : null;

    const { isLoading: isLoadingAccountDetails } =
        bankingExperience === BankingExperience.LEGACY
            ? useGetAllBankAccountDetailsLegacy(false)
            : useGetAllBankAccountDetails(false);

    const {
        fromDepositAccount,
        toDepositAccount,
        amount,
        recurring,
        applyIncomeSplit,
    } = transferRequest ?? {};

    const isRecurring = !isEmpty(recurring);
    const isInternalTransfer =
        !fromDepositAccount?.isExternal && !toDepositAccount?.isExternal;

    const confirmTransfer = async () => {
        if (!isValidTransfer()) return;

        try {
            setIsSubmitting(true);
            if (isRecurring) {
                await handleRecurringTransfer();
            } else {
                await handleOneTimeTransfer();
            }
            setTransferSubmitted(true);
        } catch (e) {
            handleTransferError(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValidTransfer = () => {
        return (
            amount != null &&
            fromDepositAccount != null &&
            toDepositAccount != null
        );
    };

    const handleRecurringTransfer = async () => {
        await createPaymentSchedule({
            body: buildRecurringTransferBody(),
        });
        handleRecurringTransferNavigation();
    };

    const buildRecurringTransferBody = (): CreatePaymentScheduleRequest => ({
        amount: amount as number,
        applyIncomeSplit,
        syncteraPersonId: user.syncteraPersonId,
        fromDepositAccount: buildDepositAccountBody(
            fromDepositAccount as DepositAccount,
        ),
        toDepositAccount: buildDepositAccountBody(
            toDepositAccount as DepositAccount,
        ),
        recurringDescription: recurring?.description ?? '',
        recurringStartDate: format(
            assertNotNullish(recurring?.start),
            DEFAULT_DATE_FORMAT,
        ),
        recurringFrequency: assertNotNullish(recurring?.frequency),
        recurringEndType: assertNotNullish(recurring?.end?.type),
        recurringEndDate: recurring?.end?.date
            ? format(recurring.end.date, DEFAULT_DATE_FORMAT)
            : undefined,
        recurringCount: recurring?.end?.count ?? undefined,
        executionTime: DEFAULT_RECURRING_EXECUTION_TIME_24H,
    });

    const buildDepositAccountBody = (account: DepositAccount) => ({
        id: account.id,
        isExternal: account.isExternal,
    });

    const handleRecurringTransferNavigation = () => {
        if (isFirstFunding) {
            setOnContinue(() => navigateToCreateEditSpace);
        } else if (context === 'bankingAdoptionChecklist') {
            router.navigate('/banking-switch/checklist');
        } else if (context === 'bankingAdoptionWidget') {
            router.navigate('/home');
        } else {
            router.navigate('/banking/recurring-transfer-success');
        }
        setTransferRequest(null);
    };

    const handleOneTimeTransfer = async () => {
        await createDepositoryTransfer({
            body: buildOneTimeTransferBody() as DepositoryTransferRequest,
        });
        handleOneTimeTransferNavigation();
    };

    const buildOneTimeTransferBody = () => ({
        amount,
        applyIncomeSplit,
        syncteraPersonId: user.syncteraPersonId,
        fromDepositAccount: buildDepositAccountBody(
            fromDepositAccount as DepositAccount,
        ),
        toDepositAccount: buildDepositAccountBody(
            toDepositAccount as DepositAccount,
        ),
    });

    const handleOneTimeTransferNavigation = () => {
        // const accountToGoTo = findAccountToNavigateTo();
        if (isFirstFunding) {
            setOnContinue(() => navigateToCreateEditSpace);
        } else if (context === 'bankingAdoptionChecklist') {
            router.navigate('/banking-switch/checklist');
        } else if (context === 'bankingAdoptionWidget') {
            router.navigate('/home');
        } else {
            setOnContinue(() => () => {
                // navigateAfterTransfer(accountToGoTo);
                // Send the member to the banking tab no matter what
                // This is a temporary workaround for ENG-3309
                router.navigate('/home/banking');
            });
        }
    };

    const navigateToCreateEditSpace = () => {
        router.navigate({
            pathname: '/banking/create-edit-space',
            params: { fromKyc: 'true' },
        });
        setTransferRequest(null);
    };

    // I am leaving these unused methods here on purpose, see comment in
    // handleOneTimeTransferNavigation for details

    // const findAccountToNavigateTo = () => {
    //     if (toDepositAccount && !toDepositAccount.isExternal) {
    //         return bankAccountDetails?.find(
    //             (s) => s?.syncteraAccountId === toDepositAccount.id,
    //         );
    //     } else if (fromDepositAccount && !fromDepositAccount.isExternal) {
    //         return bankAccountDetails?.find(
    //             (s) => s?.syncteraAccountId === fromDepositAccount.id,
    //         );
    //     }
    //     return undefined;
    // };

    // const navigateAfterTransfer = (
    //     accountToGoTo?: BankAccountDisplayDetail,
    // ) => {
    //     router.navigate(
    //         accountToGoTo
    //             ? {
    //                   pathname: '/banking/space-details',
    //                   params: {
    //                       syncteraAccountId: accountToGoTo.syncteraAccountId,
    //                       forceRefetch: 'true',
    //                   },
    //               }
    //             : { pathname: '/home/banking' },
    //     );
    // };

    const handleTransferError = (e: unknown) => {
        captureException(e);

        if (
            e instanceof FetchError &&
            // @ts-expect-error TODO FetchError json is not fully specified
            e.json?.type === 'MemberFacingError' &&
            // @ts-expect-error TODO FetchError json is not fully specified
            e.json?.subtype === 'ACH_LIMIT'
        ) {
            setIsTransferConfirmErrorSheetOpen(true);
        } else {
            toast.show('Something went wrong');
        }
    };

    if (
        !isSubmitting &&
        !transferSubmitted &&
        (fromDepositAccount == null || toDepositAccount == null)
    ) {
        // If we haven't submitted yet, and we're missing an account, go back to transfers page
        return <Redirect href="/banking/transfer" />;
    }

    if (!transferSubmitted && (isLoadingAccountDetails || isSubmitting)) {
        return <FullScreenSpinner />;
    }

    if (fromDepositAccount == null || toDepositAccount == null) {
        // Show a blank page if accounts have been cleared out from state
        // This happens when "continue" is hit on the success page, and avoids showing a flash of an empty state success page
        return (
            <ScreenContainer>
                <YStack></YStack>
            </ScreenContainer>
        );
    }

    if (transferSubmitted) {
        return (
            <TransferSuccess
                fromAccount={fromDepositAccount}
                toAccount={toDepositAccount}
                onContinue={onContinue}
                expectedArrivalDateZoned={expectedArrivalDateZoned}
            />
        );
    }

    return (
        <>
            <ScreenContainer title="Review transfer" hasBackButton>
                <YStack
                    maxWidth={500}
                    $sm={{ marginTop: 50 }}
                    alignSelf="center"
                    width="100%"
                >
                    <TransferAccountIcons
                        fromAccount={fromDepositAccount}
                        toAccount={toDepositAccount}
                    />
                    <YStack alignItems="center" gap={8}>
                        <YStack
                            width="100%"
                            justifyContent="space-between"
                            marginBottom={12}
                            borderRadius={16}
                            backgroundColor="$gray200"
                        >
                            <Private>
                                <TransferConfirmationRow
                                    title="Amount"
                                    secondaryText={centsToFormattedDollars(
                                        amount ?? 0,
                                    )}
                                />
                            </Private>
                            <TransferConfirmationRow
                                title="From"
                                secondaryText={getNormalizedDepositAccountNickname(
                                    fromDepositAccount,
                                )}
                                secondarySubtext={`${fromDepositAccount.bankName} *${fromDepositAccount.accountNumberMasked}`}
                                secondarySubtextProps={{
                                    color: '$gray1000',
                                }}
                            />
                            <TransferConfirmationRow
                                title="To"
                                secondaryText={getNormalizedDepositAccountNickname(
                                    toDepositAccount,
                                )}
                                secondarySubtext={`${toDepositAccount.bankName} *${toDepositAccount.accountNumberMasked}`}
                                secondarySubtextProps={{
                                    color: '$gray1000',
                                }}
                            />
                            {isRecurring && (
                                <TransferConfirmationRow
                                    title="Recurring"
                                    secondaryText="Yes"
                                />
                            )}
                            {isRecurring && (
                                <>
                                    <TransferConfirmationRow
                                        title="Description"
                                        secondaryText={
                                            recurring?.description ?? ''
                                        }
                                    />
                                    <TransferConfirmationRow
                                        title="Repeats"
                                        secondaryText={
                                            RECURRING_TRANSFER_FREQUENCY_DESCRIPTION_MAP[
                                                recurring?.frequency as RecurringTransferFrequency
                                            ]
                                        }
                                    />
                                    <TransferConfirmationRow
                                        title="Starts"
                                        secondaryText={format(
                                            recurring?.start ?? new Date(),
                                            SHORT_READABLE_DATE_FORMAT,
                                        )}
                                    />
                                    <TransferConfirmationRow
                                        title="Ends"
                                        secondaryText={getRecurringTransferEndText(
                                            recurring?.end?.type,
                                            recurring?.end?.date != null
                                                ? format(
                                                      recurring.end.date,
                                                      SHORT_READABLE_DATE_FORMAT,
                                                  )
                                                : undefined,
                                            recurring?.end?.count,
                                        )}
                                    />
                                </>
                            )}
                            {!toDepositAccount.isExternal && (
                                <TransferConfirmationRow
                                    title="Apply Income Split rules"
                                    secondaryText={
                                        applyIncomeSplit ? 'Yes' : 'No'
                                    }
                                />
                            )}
                            {!isRecurring &&
                                fromDepositAccount?.isExternal &&
                                !toDepositAccount?.isExternal && (
                                    <TransferConfirmationRow
                                        secondarySubtext={
                                            expectedArrivalDateZoned != null
                                                ? `${formatInTimezone(
                                                      expectedArrivalDateZoned,
                                                      SHORT_READABLE_DATE_FORMAT,
                                                  )}, ${formatInTimezone(
                                                      expectedArrivalDateZoned,
                                                      TIME_FORMAT_WITH_TIMEZONE,
                                                  )}`
                                                : ''
                                        }
                                        secondarySubtextProps={{
                                            color: '$gray1000',
                                        }}
                                        secondaryText={
                                            expectedArrivalDateZoned != null
                                                ? formatInTimezone(
                                                      expectedArrivalDateZoned,
                                                      DAY_OF_WEEK_FORMAT,
                                                  )
                                                : '-'
                                        }
                                        title="Expected arrival"
                                    />
                                )}
                        </YStack>
                        {fromDepositAccount?.accountNumberMaskedIsTokenized &&
                            fromDepositAccount?.nonTokenizedAccountNumberMasked !=
                                null && (
                                <TokenizedAccountNumberDisclosure
                                    account={fromDepositAccount}
                                    marginBottom={8}
                                />
                            )}
                        {toDepositAccount?.accountNumberMaskedIsTokenized &&
                            toDepositAccount?.nonTokenizedAccountNumberMasked !=
                                null && (
                                <TokenizedAccountNumberDisclosure
                                    account={toDepositAccount}
                                    marginBottom={8}
                                />
                            )}
                        {isRecurring ? (
                            <>
                                {isInternalTransfer ? (
                                    <RecurringInternalTransferDisclosureFootnote
                                        textAlign="left"
                                        executionTime={
                                            DEFAULT_RECURRING_EXECUTION_TIME_24H
                                        }
                                    />
                                ) : (
                                    <RecurringExternalTransferDisclosureFootnote
                                        textAlign="left"
                                        executionTime={
                                            DEFAULT_RECURRING_EXECUTION_TIME_24H
                                        }
                                        isIncoming={
                                            fromDepositAccount.isExternal
                                        }
                                    />
                                )}
                            </>
                        ) : isInternalTransfer ? (
                            <OneTimeInternalTransferDisclosureFootnote textAlign="left" />
                        ) : (
                            <OneTimeExternalTransferDisclosureFootnote
                                textAlign="left"
                                isIncoming={fromDepositAccount.isExternal}
                            />
                        )}
                    </YStack>

                    <View marginTop={isWeb ? 100 : 'auto'}>
                        <ConfirmTransferDisclosure />
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Confirm"
                            onPress={confirmTransfer}
                            disabled={isSubmitting}
                        />
                    </View>
                </YStack>
            </ScreenContainer>

            <TransferConfirmErrorSheet
                isOpen={isTransferConfirmErrorSheetOpen}
                onClose={() => {
                    setIsTransferConfirmErrorSheetOpen(false);
                }}
            />
        </>
    );
};

const TransferConfirmationRow = (props: TextRowProps) => {
    return (
        <TextRow
            containerProps={{
                borderRadius: 16,
            }}
            {...props}
        />
    );
};

interface TransferSuccessProps {
    fromAccount: DepositAccount;
    toAccount: DepositAccount;
    onContinue: () => void;
    /** ISO or API date string from ACH expected-arrival preview; omit when unavailable. */
    expectedArrivalDateZoned?: Date | null;
}

const TransferSuccess = ({
    fromAccount,
    toAccount,
    onContinue,
    expectedArrivalDateZoned,
}: TransferSuccessProps) => {
    return (
        <ScreenContainer
            hasCloseButton
            onClose={() => {
                router.navigate('/home/banking');
            }}
            containerProps={{
                justifyContent: isWeb ? 'flex-start' : 'space-between',
            }}
        >
            <YStack
                justifyContent={isWeb ? 'flex-start' : 'space-between'}
                width="100%"
                maxWidth={500}
                alignSelf="center"
            >
                <YStack
                    marginTop={isWeb ? 50 : '30%'}
                    marginBottom={isWeb ? 100 : 0}
                >
                    <View
                        alignSelf="center"
                        padding={25}
                        borderRadius={20}
                        backgroundColor="$green1000"
                        marginTop={30}
                        marginBottom={30}
                    >
                        <Icon icon={CheckIcon} color="$white" size={30} />
                    </View>
                    <H2Emphasized marginTop={16} textAlign="center">
                        Transfer initiated!
                    </H2Emphasized>
                    <BodyMediumRegular
                        textAlign="center"
                        color="$gray1000"
                        marginTop={8}
                        marginHorizontal={12}
                    >
                        {!fromAccount?.isExternal && toAccount?.isExternal
                            ? EXTERNAL_OUTGOING_TRANSFER_TIMEFRAME_DISCLOSURE_TEXT
                            : fromAccount?.isExternal && !toAccount.isExternal
                              ? expectedArrivalDateZoned != null
                                  ? `Funds are expected to arrive by\n${formatInTimezone(expectedArrivalDateZoned, DAY_OF_WEEK_FORMAT)}, ${formatReadableMonthDayInTimezone(expectedArrivalDateZoned)} at ${formatInTimezone(
                                        expectedArrivalDateZoned,
                                        TIME_FORMAT_WITH_TIMEZONE,
                                    )}.`
                                  : EXTERNAL_INCOMING_TRANSFER_TIMELINE_DISCLOSURE_TEXT
                              : ONE_TIME_INTERNAL_TRANSFER_DISCLOSURE_TEXT}
                    </BodyMediumRegular>
                </YStack>
                <View gap={8}>
                    <EBBankServicesDisclosure />
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Continue"
                        onPress={onContinue}
                    />
                </View>
            </YStack>
        </ScreenContainer>
    );
};

interface TransferConfirmErrorSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

const TransferConfirmErrorSheet = ({
    isOpen,
    onClose,
}: TransferConfirmErrorSheetProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Transfer could not be completed"
            showCloseButton
        >
            <YStack marginTop={16}>
                <BodyMediumRegular marginTop={16}>
                    Whoops! Your attempted transfer exceeds Fruitful&apos;s
                    limits and cannot be processed. You can read more about our
                    transfer limits in the Deposit Account Agreement located at{' '}
                    <Link href="https://www.fruitful.com/legal" target="_blank">
                        <BodyMediumRegular
                            textDecorationLine="underline"
                            color="$green1200"
                        >
                            fruitful.com/legal
                        </BodyMediumRegular>
                    </Link>{' '}
                    or reach out to us at support@fruitful.com.
                </BodyMediumRegular>
            </YStack>
        </Sheet>
    );
};

export default TransferConfirmation;
